import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { resetForcedPassword } from 'src/models/user/user';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

interface ForceResetPasswordDialogProps {
  onSuccess: () => Promise<void>;
}

const ForceResetPasswordDialog: React.FC<ForceResetPasswordDialogProps> = ({
  onSuccess,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'forceResetPassword',
  });
  const [submitError, setSubmitError] = useState<string | null>(null);

  return (
    <Formik
      initialValues={{
        currentPassword: '',
        password: '',
        passwordConfirmation: '',
      }}
      validationSchema={Yup.object({
        currentPassword: Yup.string().required(t('currentPasswordRequired')),
        password: Yup.string().required(t('passwordRequired')),
        passwordConfirmation: Yup.string()
          .required(t('passwordConfirmationRequired'))
          .oneOf([Yup.ref('password')], t('passwordsMustMatch')),
      })}
      onSubmit={async (values, helpers) => {
        setSubmitError(null);
        try {
          await resetForcedPassword({
            currentPassword: values.currentPassword,
            password: values.password,
            passwordConfirmation: values.passwordConfirmation,
          });
          await onSuccess();
          toast.success(t('success'));
          helpers.resetForm();
        } catch (error) {
          setSubmitError(api.formatError(error, t('error')));
        }
      }}
    >
      {({ submitForm, isSubmitting }) => (
        <Dialog
          open
          title={t('title')}
          actions={[
            {
              label: isSubmitting ? t('submitting') : t('submit'),
              onClick: submitForm,
              variant: 'prominent',
              disabled: isSubmitting,
            },
          ]}
        >
          <Form>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t('description')}
              </Typography>
              {submitError && (
                <Typography variant="body2" color="error">
                  {submitError}
                </Typography>
              )}
              <FormTextField
                name="currentPassword"
                label={t('currentPassword')}
                type="password"
                disabled={isSubmitting}
              />
              <FormTextField
                name="password"
                label={t('newPassword')}
                type="password"
                disabled={isSubmitting}
              />
              <FormTextField
                name="passwordConfirmation"
                label={t('confirmPassword')}
                type="password"
                disabled={isSubmitting}
              />
            </Stack>
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};

export default ForceResetPasswordDialog;
